import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getInitialSearch, clearInitialSearch } from '../utils/loginPrefill';

// Add CSS for animations and hover effects
const injectStyles = () => {
  const styleId = 'login-styles';
  if (document.getElementById(styleId)) return;
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    [data-login-input]:focus {
      border-color: #667eea !important;
      box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.15) !important;
      outline: none;
    }
    
    [data-login-button]:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4) !important;
    }
    
    [data-login-button]:active:not(:disabled) {
      transform: translateY(0) scale(0.98);
    }
    
    [data-login-link]:hover {
      text-decoration: underline;
    }
  `;
  document.head.appendChild(style);
};

const Login = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    injectStyles();
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [formData, setFormData] = useState({
    prolific_id: '',
    real_prolific_id: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [autoSubmit, setAutoSubmit] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  // Pre-fill the form from URL query parameters so participants can be given a
  // one-click login link. All params are optional and case-insensitive keys are
  // supported. Examples:
  //   ?u=g1&pw=Guess1234
  //   ?username=g1&password=Guess1234&pid=5abc12de34&auto=1
  // Field mapping:
  //   u / username / provided_username -> Provided Username (login credential)
  //   pw / password / pass             -> password
  //   pid / prolific_id / PROLIFIC_PID -> Prolific ID (saved each login)
  //   auto=1                            -> submit automatically when all present
  // Sensitive params are removed from the URL immediately so the password does
  // not persist in the address bar or browser history.
  useEffect(() => {
    // Read the query snapshot taken at page load (the router strips the live URL
    // before this runs), falling back to the current URL if a snapshot is absent.
    const rawSearch = getInitialSearch() || window.location.search;
    const params = new URLSearchParams(rawSearch);
    const get = (...keys) => {
      for (const k of keys) {
        const v = params.get(k);
        if (v != null && v !== '') return v;
      }
      return null;
    };
    const u = get('u', 'username', 'provided_username');
    const pw = get('pw', 'password', 'pass');
    const pid = get('pid', 'prolific_id', 'PROLIFIC_PID');

    if (u || pw || pid) {
      setFormData(prev => ({
        ...prev,
        ...(u ? { prolific_id: u } : {}),
        ...(pw ? { password: pw } : {}),
        ...(pid ? { real_prolific_id: pid } : {}),
      }));
      // Strip params from the URL without reloading (keeps password out of history).
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    }

    if (params.get('auto') === '1' && u && pw && pid) {
      setAutoSubmit(true);
    }

    // Consume the snapshot so it cannot re-fill / re-submit on a later visit.
    clearInitialSearch();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.prolific_id.trim()) {
      newErrors.prolific_id = 'Provided Username is required';
    }

    // Prolific ID: any non-empty value is accepted
    if (!formData.real_prolific_id.trim()) {
      newErrors.real_prolific_id = 'Prolific ID is required';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const result = await login(formData);

    if (result.success) {
      // Let the PersonalityGuard handle the redirect logic
      navigate('/chat');
    } else {
      setErrors({ submit: result.error });
    }
    setLoading(false);
  };

  // Fire the auto-submit once the URL-provided values have been written to state.
  useEffect(() => {
    if (!autoSubmit) return;
    setAutoSubmit(false);
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div style={{
      ...styles.container,
      padding: isMobile ? '16px' : '20px'
    }}>
      <div style={{
        ...styles.card,
        borderRadius: isMobile ? '16px' : '24px',
        padding: isMobile ? '32px 20px' : '48px',
        maxWidth: isMobile ? '100%' : '440px',
        margin: isMobile ? '0 8px' : '0',
        boxSizing: 'border-box'
      }}>
        <div style={styles.iconContainer}>
          <div style={{
            ...styles.icon,
            width: isMobile ? 52 : 64,
            height: isMobile ? 52 : 64,
            fontSize: isMobile ? 26 : 32
          }}>💬</div>
        </div>
        <h2 style={{
          ...styles.title,
          fontSize: isMobile ? '26px' : '32px'
        }}>Welcome Back</h2>
        <p style={{
          ...styles.subtitle,
          fontSize: isMobile ? '14px' : '15px',
          marginBottom: isMobile ? '24px' : '32px'
        }}>Sign in to continue your conversations</p>
        
        {errors.submit && (
          <div style={styles.errorMessage}>
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{
          ...styles.form,
          gap: isMobile ? '16px' : '20px'
        }}>
          <div style={styles.inputGroup}>
            <label style={{
              ...styles.label,
              fontSize: isMobile ? '13px' : '14px'
            }}>Provided Username</label>
            <input
              type="text"
              name="prolific_id"
              value={formData.prolific_id}
              onChange={handleChange}
              autoCapitalize="none"
              autoCorrect="off"
              style={{
                ...styles.input,
                ...(errors.prolific_id ? styles.inputError : {}),
                padding: isMobile ? '12px 16px' : '14px 18px',
                fontSize: isMobile ? '15px' : '16px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              placeholder="Enter your provided username"
              data-login-input
            />
            {errors.prolific_id && <span style={styles.errorText}>{errors.prolific_id}</span>}
          </div>

          <div style={styles.inputGroup}>
            <label style={{
              ...styles.label,
              fontSize: isMobile ? '13px' : '14px'
            }}>Prolific ID</label>
            <input
              type="text"
              name="real_prolific_id"
              value={formData.real_prolific_id}
              onChange={handleChange}
              autoCapitalize="none"
              autoCorrect="off"
              style={{
                ...styles.input,
                ...(errors.real_prolific_id ? styles.inputError : {}),
                padding: isMobile ? '12px 16px' : '14px 18px',
                fontSize: isMobile ? '15px' : '16px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              placeholder="Enter your Prolific ID"
              data-login-input
            />
            {errors.real_prolific_id && <span style={styles.errorText}>{errors.real_prolific_id}</span>}
          </div>

          <div style={styles.inputGroup}>
            <label style={{
              ...styles.label,
              fontSize: isMobile ? '13px' : '14px'
            }}>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              style={{
                ...styles.input,
                ...(errors.password ? styles.inputError : {}),
                padding: isMobile ? '12px 16px' : '14px 18px',
                fontSize: isMobile ? '15px' : '16px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              placeholder="Enter your password"
              data-login-input
            />
            {errors.password && <span style={styles.errorText}>{errors.password}</span>}
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {}),
              padding: isMobile ? '14px 24px' : '14px 28px',
              fontSize: isMobile ? '16px' : '17px'
            }}
            data-login-button
          >
            {loading ? '⏳ Signing In...' : '🚀 Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '24px',
    padding: '48px',
    boxShadow: '0 25px 50px rgba(102, 126, 234, 0.25)',
    width: '100%',
    maxWidth: '440px',
    animation: 'fadeIn 0.6s ease-out',
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 20,
  },
  icon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 32,
    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
  },
  title: {
    fontSize: '32px',
    fontWeight: 700,
    textAlign: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    marginBottom: '8px',
  },
  subtitle: {
    textAlign: 'center',
    color: '#6b7280',
    marginBottom: '32px',
    fontSize: '15px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  input: {
    padding: '14px 18px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '16px',
    transition: 'all 0.3s ease',
    backgroundColor: '#f9fafb',
  },
  inputError: {
    borderColor: '#ef4444',
    boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
  },
  errorText: {
    fontSize: '12px',
    color: '#ef4444',
    marginTop: '4px',
  },
  errorMessage: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  button: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '14px 28px',
    border: 'none',
    borderRadius: '12px',
    fontSize: '17px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '10px',
    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
  },
  buttonDisabled: {
    background: '#9ca3af',
    cursor: 'not-allowed',
    boxShadow: '0 2px 8px rgba(156, 163, 175, 0.2)',
  },
  footer: {
    textAlign: 'center',
    marginTop: '24px',
    color: '#6b7280',
  },
  link: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    textDecoration: 'none',
    fontWeight: 600,
  },
};

export default Login;
